import type { Locale } from 'date-fns';
import {
  de, enAU, enCA, enGB, enIE, enNZ, enUS, es, fr, it, nl, pl, pt, ptBR,
} from 'date-fns/locale';

// MUI's AdapterDateFns defaults to date-fns' built-in en-US locale, which renders
// MM/DD/YYYY for every user. Resolve the device locale instead so pickers match the
// iOS/system region, falling back to en-GB (the app's primary audience).
const LOCALES: Record<string, Locale> = {
  'en-au': enAU,
  'en-ca': enCA,
  'en-gb': enGB,
  'en-ie': enIE,
  'en-nz': enNZ,
  'en-us': enUS,
  de, en: enGB, es, fr, it, nl, pl, pt,
  'pt-br': ptBR,
};

export const resolveDateFnsLocale = (tag?: string): Locale => {
  if (!tag) return enGB;
  const lower = tag.toLowerCase();
  return LOCALES[lower] ?? LOCALES[lower.split('-')[0]] ?? enGB;
};

/**
 * Intl is consulted before navigator.language because the iOS WKWebView reports the app's
 * bundle localization, not the device region: with only CFBundleDevelopmentRegion=en and no
 * CFBundleLocalizations, navigator.language can read "en-US" on a UK device. Intl resolves
 * from the system locale instead, so it carries the real region.
 */
const getSystemLocaleTag = (): string | undefined => {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().locale;
    if (resolved && resolved.includes('-')) return resolved;
  } catch {
    // Fall through to navigator.
  }
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return undefined;
};

export const getDateFnsLocale = (): Locale => resolveDateFnsLocale(getSystemLocaleTag());

export const dateFnsLocale = getDateFnsLocale();
